import React, { useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';
import { useWebSocket } from '../WebSocketContext';
import './JourneyList.css';
import JourneyEditModal from './JourneyEditModal';

const apiUrl = process.env.REACT_APP_API_URL;

const JourneyList = ({ journeys = [], onJourneySelect, selectedJourneyId, onJourneyUpdate, calculatingJourneyIds = new Set() }) => {
  const [editingJourney, setEditingJourney] = useState(null);
  const { connectionId } = useWebSocket(); // Get connectionId for journey-progress API calls

  const getUserFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return null;
    }
  };

  const updateJourneyProperty = async (journey, property, value) => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const user = getUserFromToken(token);

      if (!user) {
        throw new Error('Failed to retrieve user from JWT token.');
      }

      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
      
      // Use local state to construct journeys array instead of GET request
      // Extract user journey metadata from local journeys prop
      const existingJourneys = journeys.map(j => ({
        elementID: j.journeyID,
        journeyColor: j.journeyColor,
        displayItemsMode: j.displayItemsMode,
        overrides: j.overrides || [],
        isActive: j.isActive,
        manualPercentComplete: j.manualPercentComplete,
        displayJourney: j.displayJourney,
        isComplete: j.isComplete,
        isFavorite: j.isFavorite
      }));

      const journeyIndex = existingJourneys.findIndex(
        j => j.elementID === journey.journeyID
      );

      if (journeyIndex < 0) {
        throw new Error('Journey not found in user journeys');
      }

      const updatedJourney = {
        ...existingJourneys[journeyIndex],
        [property]: value
      };

      const updatedJourneys = [...existingJourneys];
      updatedJourneys[journeyIndex] = updatedJourney;

      const patchData = {
        journeys: updatedJourneys
      };

      await axios.patch(url, patchData, { headers });

      // Update UI immediately with the changed property
      // Pass the updated property along with journeyID so MainApp can update just that journey
      // WebSocket will also update it, but this provides instant feedback
      if (onJourneyUpdate) {
        await onJourneyUpdate({
          journeyID: journey.journeyID,
          [property]: value
        });
      }
    } catch (error) {
      console.error(`Error updating journey ${property}:`, error);
      alert(`Failed to update journey ${property}. Please try again.`);
    }
  };

  const updateJourneyProperties = async (journey, properties) => {
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const user = getUserFromToken(token);

      if (!user) {
        throw new Error('Failed to retrieve user from JWT token.');
      }

      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
      
      // Use local state to construct journeys array instead of GET request
      // Extract user journey metadata from local journeys prop
      const existingJourneys = journeys.map(j => ({
        elementID: j.journeyID,
        journeyColor: j.journeyColor,
        displayItemsMode: j.displayItemsMode,
        overrides: j.overrides || [],
        isActive: j.isActive,
        manualPercentComplete: j.manualPercentComplete,
        displayJourney: j.displayJourney,
        isComplete: j.isComplete,
        isFavorite: j.isFavorite
      }));

      const journeyIndex = existingJourneys.findIndex(
        j => j.elementID === journey.journeyID
      );

      if (journeyIndex < 0) {
        throw new Error('Journey not found in user journeys');
      }

      const updatedJourney = {
        ...existingJourneys[journeyIndex],
        ...properties
      };

      const updatedJourneys = [...existingJourneys];
      updatedJourneys[journeyIndex] = updatedJourney;

      const patchData = {
        journeys: updatedJourneys
      };

      await axios.patch(url, patchData, { headers });

      // Update UI immediately with the changed properties
      // Pass the updated properties along with journeyID so MainApp can update just that journey
      // WebSocket will also update it, but this provides instant feedback
      if (onJourneyUpdate) {
        await onJourneyUpdate({
          journeyID: journey.journeyID,
          ...properties
        });
      }
    } catch (error) {
      console.error('Error updating journey properties:', error);
      alert('Failed to update journey. Please try again.');
    }
  };

  const handleFavoriteClick = async (e, journey) => {
    e.stopPropagation();
    await updateJourneyProperty(journey, 'isFavorite', !journey.isFavorite);
  };

  const handleCompleteClick = async (e, journey) => {
    e.stopPropagation();
    const newCompleteStatus = !journey.isComplete;
    
    // If marking as complete, also set manualPercentComplete to 100%
    if (newCompleteStatus) {
      await updateJourneyProperties(journey, {
        isComplete: true,
        manualPercentComplete: 100
      });
    } else {
      await updateJourneyProperty(journey, 'isComplete', false);
    }
  };

  const handleDisplayJourneyClick = async (e, journey) => {
    e.stopPropagation();
    const newValue = !journey.displayJourney;
    
    // If trying to set to true, check if we're already at the max (3)
    if (newValue) {
      const currentlyDisplayed = journeys.filter(j => j.displayJourney === true).length;
      if (currentlyDisplayed >= 3) {
        alert('Maximum of 3 journeys can be displayed at once. Please disable another journey first.');
        return;
      }
    }
    
    await updateJourneyProperty(journey, 'displayJourney', newValue);
  };

  const handleActiveClick = async (e, journey) => {
    e.stopPropagation();
    const newValue = !journey.isActive;
    
    // If trying to set to true, check if we're already at the max (3)
    if (newValue) {
      const currentlyActive = journeys.filter(j => j.isActive === true).length;
      if (currentlyActive >= 3) {
        alert('Maximum of 3 journeys can be active at once. Please deactivate another journey first.');
        return;
      }
    }
    
    await updateJourneyProperty(journey, 'isActive', newValue);
  };

  const handleEditClick = (e, journey) => {
    e.stopPropagation();
    setEditingJourney(journey);
  };

  const handleCloseEdit = () => {
    setEditingJourney(null);
  };

  const handleSaveJourney = async (updatedJourney) => {
    if (onJourneyUpdate) {
      await onJourneyUpdate(updatedJourney);
    }
    setEditingJourney(null);
  };

  const handleRefresh = async (e, journey) => {
    e.stopPropagation();
    
    const confirmed = window.confirm(
      'Recalculating journey progress may take several minutes depending on the size of your journey and number of notes. ' +
      'The process runs in the background—you can continue using the app while it completes. ' +
      'Continue?'
    );
    if (!confirmed) return;
    
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const user = getUserFromToken(token);

      if (!user) {
        throw new Error('Failed to retrieve user from JWT token.');
      }

      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
      
      // Get connectionId from WebSocket context
      const wsConnectionId = connectionId || '';
      
      // Build journeys array with ownerID - use existing ownerID if available, otherwise fetch it
      const journeysWithOwnerID = await Promise.all(
        journeys.map(async (j) => {
          // If journey already has ownerID, use it
          if (j.ownerID) {
            return {
              journeyID: j.journeyID,
              ownerID: j.ownerID
            };
          }
          
          // Otherwise, fetch journey details to get ownerID
          try {
            // Use dataType from journey if available, otherwise don't specify
            const dataType = j.dataType ? `?dataType=${j.dataType}` : '';
            const journeyResponse = await axios.get(`${apiUrl}/journeys/${j.journeyID}${dataType}`, { headers });
            const journeyData = journeyResponse.data;
            return {
              journeyID: j.journeyID,
              ownerID: journeyData.ownerID || 'System'
            };
          } catch (error) {
            // If fetch fails, use System as default
            console.warn(`Could not fetch ownerID for journey ${j.journeyID}, defaulting to System:`, error);
            return {
              journeyID: j.journeyID,
              ownerID: 'System'
            };
          }
        })
      );
      
      // Build POST body with all journeys
      const postBody = {
        journeys: journeysWithOwnerID.filter(j => j.journeyID), // Only include journeys with journeyID
        clientId: wsConnectionId
      };
      
      // POST request to refresh journey progress
      const response = await axios.post(
        `${url}/journey-progress?sendWebSocket=true`,
        postBody,
        { headers }
      );
      
      if (response.status === 200 || response.status === 201) {
        console.log('Journey progress refresh initiated successfully');
        alert('Refresh started! The calculation is running in the background. You can continue using the app—progress will update when it completes.');
      }
    } catch (error) {
      console.error('Error refreshing journey progress:', error);
      alert('Failed to refresh journey progress. Please try again.');
    }
  };

  const handleRemoveJourney = async (e, journey) => {
    e.stopPropagation();
    
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to remove "${journey.title || journey.tripName || journey.name || 'this journey'}" from your Explorer Board?`)) {
      return;
    }

    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const user = getUserFromToken(token);

      if (!user) {
        throw new Error('Failed to retrieve user from JWT token.');
      }

      const headers = {
        'Content-type': 'application/json; charset=UTF-8',
        'Authorization': `Bearer ${token}`
      };

      const url = `${apiUrl}/users/${encodeURIComponent(user)}`;
      
      // Fetch current user data to get both journeys and journeyProgress
      const getUserResponse = await axios.get(`${url}?journeyComplete=true`, { headers });
      const currentUserData = Array.isArray(getUserResponse.data) ? getUserResponse.data[0] : getUserResponse.data;
      const existingJourneys = currentUserData?.journeys || [];
      
      // Get journeyProgress structure
      const journeyProgressObj = currentUserData?.journeyProgress || {};
      const journeyProgressArray = journeyProgressObj?.journeyProgress || [];
      
      // Remove journey from journeys array
      const updatedJourneys = existingJourneys.filter(j => j.elementID !== journey.journeyID);
      
      // Remove journey from journeyProgress array
      const updatedJourneyProgressArray = journeyProgressArray.filter(p => p.journeyID !== journey.journeyID);
      
      // Reconstruct journeyProgress object with updated array
      const updatedJourneyProgress = {
        ...journeyProgressObj,
        journeyProgress: updatedJourneyProgressArray
      };
      
      // Create PATCH payload with both journeys and journeyProgress updated
      const patchData = {
        journeys: updatedJourneys,
        journeyProgress: updatedJourneyProgress
      };

      await axios.patch(url, patchData, { headers });

      // Update UI immediately by calling onJourneyUpdate with a special flag to remove
      // The parent component (MainApp) should handle removing it from the journeys state
      if (onJourneyUpdate) {
        // Pass a special flag to indicate removal
        await onJourneyUpdate({
          journeyID: journey.journeyID,
          _remove: true
        });
      }
    } catch (error) {
      console.error('Error removing journey:', error);
      alert('Failed to remove journey. Please try again.');
    }
  };

  return (
    <div className="journey-list">
      {journeys.map((journey) => {
        const journeyId = journey.journeyID || journey.id;
        const journeyColor = journey.journeyColor;
        const borderColorStyle = journeyColor ? {
          borderLeftWidth: '4px',
          borderLeftStyle: 'solid',
          borderLeftColor: `rgb(${journeyColor.r}, ${journeyColor.g}, ${journeyColor.b})`
        } : {};
        
        return (
          <div
            key={journeyId}
            className={`journey-card ${selectedJourneyId === journeyId ? 'selected' : ''}`}
            onClick={() => onJourneySelect && onJourneySelect(journeyId)}
            style={borderColorStyle}
          >
            <div className="journey-card-header">
              <div className="journey-header-left">
                <h3 className="journey-name">{journey.title || journey.tripName || journey.name || 'Untitled Journey'}</h3>
                {calculatingJourneyIds.has(journeyId) && (
                  <span className="calculating-badge" title="Journey progress is being calculated. This may take a few minutes.">
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Calculating...</span>
                  </span>
                )}
              </div>
              <div className="journey-header-right">
                {journey.locationCount !== undefined && journey.locationCount !== null && (
                  <span className="location-count-badge">
                    <i className="fas fa-map-marker-alt"></i> {journey.locationCount}
                  </span>
                )}
                <button
                  className={`action-btn favorite-btn ${journey.isFavorite ? 'active' : ''}`}
                  onClick={(e) => handleFavoriteClick(e, journey)}
                  title={journey.isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                >
                  <i className="fas fa-heart"></i>
                </button>
                
                <button
                  className={`action-btn complete-btn ${journey.isComplete ? 'active' : ''}`}
                  onClick={(e) => handleCompleteClick(e, journey)}
                  title={journey.isComplete ? 'Mark as Incomplete' : 'Mark as Complete'}
                >
                  <i className="fas fa-check"></i>
                </button>
                
                <button
                  className={`action-btn display-btn ${journey.displayJourney ? 'active' : ''}`}
                  onClick={(e) => handleDisplayJourneyClick(e, journey)}
                  title={journey.displayJourney ? 'Hide from Map' : 'Display on Map'}
                >
                  <i className="fas fa-eye"></i>
                </button>
                
                <button
                  className={`action-btn active-btn ${journey.isActive ? 'active' : ''}`}
                  onClick={(e) => handleActiveClick(e, journey)}
                  title={journey.isActive ? 'Set Inactive: Deactivate this journey. Inactive journeys do not automatically calculate progress.' : 'Set Active: Required for automatic progress calculation. Only active journeys track your completion percentage. Maximum 3 journeys can be active at once.'}
                  style={journey.isActive ? { color: '#007bff' } : {}}
                >
                  <i className="fas fa-play-circle"></i>
                </button>
                
                <button
                  className="action-btn refresh-icon"
                  onClick={(e) => handleRefresh(e, journey)}
                  title="Recalculate Progress: Manually refresh journey progress calculation. This compares your notes with journey locations and updates the completion percentage. May take a few moments."
                  style={{ opacity: '1', color: 'inherit' }}
                >
                  <i className="fas fa-sync-alt" style={{ opacity: '1', color: 'inherit' }}></i>
                  <span className="fa-fallback" style={{ display: 'none', fontSize: '14px' }}>↻</span>
                </button>
                
                <button
                  className="journey-edit-btn"
                  onClick={(e) => handleEditClick(e, journey)}
                  title="Edit Journey Settings"
                >
                  <i className="fas fa-cog"></i>
                </button>
                
                <button
                  className="action-btn remove-btn"
                  onClick={(e) => handleRemoveJourney(e, journey)}
                  title="Remove Journey from Explorer Board"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
            
            {journey.description && (
              <p className="journey-description">{journey.description}</p>
            )}
            
            <div className="journey-quick-info">
              {(journey.percentComplete !== undefined || journey.manualPercentComplete !== undefined) && (
                <div className="journey-progress">
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar-fill"
                      style={{ width: `${journey.percentComplete !== undefined ? journey.percentComplete : journey.manualPercentComplete}%` }}
                    ></div>
                  </div>
                  <span className="progress-text">
                    {(() => {
                      const percent = journey.percentComplete !== undefined ? journey.percentComplete : journey.manualPercentComplete;
                      if (percent > 0 && percent < 0.1) {
                        return percent.toFixed(2) + '% Complete';
                      } else if (percent === 0) {
                        return '0.0% Complete';
                      } else {
                        return percent.toFixed(1) + '% Complete';
                      }
                    })()}
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })}
      
      {editingJourney && (
        <JourneyEditModal
          journey={editingJourney}
          isOpen={!!editingJourney}
          onClose={handleCloseEdit}
          onSave={handleSaveJourney}
        />
      )}
    </div>
  );
};

export default JourneyList;
